import { auth, email, store } from '@/firebase'
import { FormulaUser } from '@/helpers/fs/formulaUser'
import {
  CollectionName,
  FSUserPublicProfile,
  WithId,
  WithUid
} from '@WombatTechnology/nitte-time-lib'
import { isEmpty, isNull, isUndefined } from 'es-toolkit/compat'
import {
  CollectionReference,
  DocumentData,
  DocumentReference,
  collection,
  doc,
  getDoc
} from 'firebase/firestore'
import { UserNotFoundError } from '../bookingPageError'
import { toData } from './toData'
import { UserPlan } from './userPlan'
import { AuthorizedService } from './users/authorizedService'

export class UserPublicProfile {
  static doc(uid: string) {
    return doc<FSUserPublicProfile, DocumentData>(
      this.collection(),
      uid
    ) as DocumentReference<FSUserPublicProfile>
  }
  static collection() {
    return collection(
      store,
      CollectionName.UserPublicProfiles
    ) as CollectionReference<FSUserPublicProfile>
  }

  static loginUserDoc() {
    if (isNull(auth.currentUser)) {
      return null
    }
    return this.doc(auth.currentUser.uid)
  }

  static async loginUserProfile(): Promise<WithUid<FSUserPublicProfile> | null> {
    const doc = this.loginUserDoc()
    if (isNull(doc)) {
      return null
    }
    const ds = await getDoc(doc)
    return { uid: ds.ref.id, ...(ds.data() as FSUserPublicProfile) }
  }

  static isCalendarScopeAuthorized(data: FSUserPublicProfile | null) {
    const scope = data?.scope

    if (data?.accountType === 'ms') {
      // NOTE:
      // MSの場合は部分的に承認することができないので、基本true
      // ただし、認証が切れた場合にScopeを空にしているので、その場合はfalse
      return !isEmpty(scope)
    }
    const hasV1Access = scope?.includes(
      'https://www.googleapis.com/auth/calendar'
    )
    const hasV2Access =
      scope?.includes('https://www.googleapis.com/auth/calendar.events') &&
      scope?.includes('https://www.googleapis.com/auth/calendar.readonly')
    return hasV1Access || hasV2Access
  }

  static isRoomAvailable(data: FSUserPublicProfile | null) {
    if (data?.accountType === 'google') return true
    const scope = data?.scope
    // MSの場合、Place.Read.Allの権限が必要
    return scope?.includes('Place.Read.All')
  }

  static async find(uid: string): Promise<WithId<FSUserPublicProfile>> {
    const ds = await getDoc(UserPublicProfile.doc(uid))
    if (isUndefined(ds.data())) {
      throw new UserNotFoundError(uid)
    }
    return {
      id: ds.ref.id,
      ...(ds.data() as FSUserPublicProfile)
    }
  }

  static async findMayBeNull(
    uid: string
  ): Promise<WithId<FSUserPublicProfile> | null> {
    try {
      const profile = await this.find(uid)
      return profile
    } catch (error) {
      return null
    }
  }

  static async finds(uids: string[]): Promise<WithId<FSUserPublicProfile>[]> {
    return Promise.all(uids.map(async (uid) => this.find(uid)))
  }

  static async toFormulaUser(
    profile: WithUid<FSUserPublicProfile>
  ): Promise<FormulaUser> {
    const [authorizedServices, userPlan] = await Promise.all([
      AuthorizedService.authorizedServices(profile.uid),
      toData(UserPlan.doc(profile.uid))
    ])
    return {
      id: profile.uid,
      name: profile?.name,
      email: email()!,
      icon: profile?.icon,
      type: 'user',
      authorizedServices,
      plan: userPlan.plan
    }
  }

  static MediatoryAdminProfile(): FSUserPublicProfile {
    return {
      userId: 'mediatory_admin',
      icon: '',
      name: '管理者',
      companyName: '管理者',
      accountType: 'google',
      hasMultiAccounts: false,
      msAccountType: null,
      scope: []
    }
  }
}
